/*
 * The answer matrix. Question rows, product columns, grouped by topic.
 *
 * It is deliberately its own table rather than the shared `Table` component:
 * that one takes a flat row list, and this needs a `tbody` per topic with a
 * heading row and a trailing average, plus a sticky first column so a long
 * question stays readable while scrolling sideways.
 */
.scroll {
  overflow-x: auto;

  background-color: var(--surface);

  border: 1px solid var(--border);
  border-radius: var(--radius-m);
}

.matrix {
  width: 100%;

  border-collapse: collapse;
}

.head {
  position: sticky;
  top: 0;
  z-index: 2;

  padding: var(--space-s);

  font-size: var(--size-100);
  font-weight: 600;
  text-align: start;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  color: var(--text-muted);
  background-color: var(--surface-sunken);

  border-block-end: 1px solid var(--border-strong);
}

.product {
  min-width: 8rem;

  text-align: center;
}

/* The topic this run of rows hangs off, spanning the full width. */
.groupHead {
  display: table-cell;

  padding: var(--space-s);

  font-size: var(--size-200);
  font-weight: 600;
  text-align: start;

  color: var(--node-capability-text);
  background-color: var(--node-capability-surface);
}

.groupHint {
  display: block;

  max-width: 60ch;
  margin-block-start: var(--space-2xs);

  font-size: var(--size-100);
  font-weight: 400;

  color: var(--band-missing);
}

.row:hover {
  background-color: rgb(255 255 255 / 4%);
}

/*
 * The question, kept in view while the products scroll past it. Coloured like a
 * question node in the catalog tree, so the two screens name the same thing the
 * same way.
 */
.questionCell {
  position: sticky;
  inset-inline-start: 0;
  z-index: 1;

  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-xs);

  max-width: 34rem;
  padding: var(--space-xs) var(--space-s);

  font-weight: 400;
  text-align: start;

  color: var(--node-question-text);
  background-color: var(--node-question-surface);

  border-block-end: 1px solid var(--border);
}

.questionTitle {
  font-weight: var(--weight-medium);
}

/* Same shape as the catalog tree's coverage pill, for the same reason. */
.badge {
  padding: 0 var(--space-xs);

  font-size: var(--size-100);
  font-weight: 600;

  border: 1px solid currentColor;
  border-radius: var(--radius-pill);
}

.counted {
  opacity: 0.75;
}

/* Answered, yet no report reaches it. The state worth interrupting the reader for. */
.outside {
  color: var(--band-missing);

  border-width: 2px;
}

.unanswered {
  color: var(--text-faint);

  border-style: dashed;
}

.cell {
  padding: var(--space-xs) var(--space-s);

  text-align: center;
  vertical-align: middle;

  border-block-end: 1px solid var(--border);
}

.answer {
  font-variant-numeric: tabular-nums;
  font-weight: var(--weight-medium);
}

.text {
  font-size: var(--size-100);
  font-weight: 400;

  color: var(--text-muted);
}

/* Answers exist but none of them has been resolved, so no value is claimed. */
.recorded {
  font-size: var(--size-100);

  color: var(--band-missing);
}

.absent {
  color: var(--text-faint);
}

.averageRow {
  background-color: var(--surface-sunken);
}

.mean {
  display: block;

  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* How much of the topic the mean actually rests on, so a thin one looks thin. */
.thinness {
  font-size: var(--size-100);

  color: var(--text-faint);
}
